/**
 * Custom hook untuk mengambil daftar inventory dengan pagination
 * 
 * API Endpoint: GET `/api/v4/inventory`
 * 
 * @param {object} options - Options for the hook
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { InventoryResponseSchema } from "../response/IndexInventoryResponse";

const API_VERSION = 'v4';

interface UseIndexInventoryProps {
    search?: string;
    filter?: string;
    page?: number;
    limit?: number;
    include?: string;
}

const useIndexInventory = ({ search, filter, page = 1, limit = 10, include }: UseIndexInventoryProps) => {
    return useQuery({
        queryKey: ['inventories', search, filter, page, limit, include],
        queryFn: async () => {
            try {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (filter) params.append('filter', filter);
                params.append('page', page.toString());
                params.append('limit', limit.toString());
                if (include) params.append('include', include);

                const response = await privateApi.get(`/${API_VERSION}/inventory?${params.toString()}`);

                // Validasi response
                const validationResult = InventoryResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid inventory data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch inventories`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useIndexInventory;
